import { NotificationSettingsDialog } from '@strata/react-dialog';
import { SuccessToast } from '@strata/react-toast-message';
import { type ComponentProps, useEffect, useMemo } from 'react';

import { NewLiveReferralSettings } from 'components/header/dialogs/notificationSettings/NewLiveReferralSettings';
import { WorklistSummarySettings } from 'components/header/dialogs/notificationSettings/WorklistSummarySettings';
import { NotificationTypes } from 'components/notifications/NotificationComponentMap';
import {
    useGetNotificationPreferencesQuery,
    usePostUpdateNotificationPreferencesMutation,
} from 'store/api/notificationApi';

const summaryEmailTypes: ComponentProps<typeof NotificationSettingsDialog>['summaryEmailTypes'] = [
    {
        value: NotificationTypes.WORKSUM,
        text: 'Daily Worklist Summary',
        description: 'Summary of outstanding tasks, delivered in the morning.',
        AdditionalSettings: <WorklistSummarySettings />,
        additionalSettingsCallback: (formik, values) =>
            formik.setFieldValue(
                'worklistSummaryCategories',
                values.includes(NotificationTypes.WORKSUM)
                    ? WorklistSummarySettings.Options.map(({ value }) => value)
                    : [],
            ),
    },
    {
        value: NotificationTypes.LIVEREFSUM,
        text: 'Live Referral Summary',
        description:
            'Available opportunities, delivered 3 times a day to cover all Live Referrals.',
    },
    {
        value: NotificationTypes.WONREFSUM,
        text: 'End of Day Referral Recap',
        description:
            'Opportunities obtained during the day, delivered at the end of business hours.',
    },
];

const notificationTypes = [
    {
        value: NotificationTypes.NEW_REFER,
        text: 'There is a new Live Referral from specific prescribers',
        AdditionalSettings: <NewLiveReferralSettings />,
    },
    {
        value: NotificationTypes.END_SOON,
        text: "A referral I've bid on is ending soon",
    },
    {
        value: NotificationTypes.OUTBID,
        text: 'I have been outbid on a referral',
    },
    {
        value: NotificationTypes.PENDINTAKE,
        text: 'A referral is pending intake',
    },
    {
        value: NotificationTypes.VCATTACHED,
        text: 'A value case has been added to a referral I service',
    },
];

interface UserNotificationSettingsDialogProps {
    isOpen: boolean;
    onSecondaryButtonClick: () => void;
}

export const UserNotificationSettingsDialog = ({
    isOpen,
    onSecondaryButtonClick,
}: UserNotificationSettingsDialogProps) => {
    const [postUpdateNotificationPreferences] = usePostUpdateNotificationPreferencesMutation();
    const { data, isFetching, isError } = useGetNotificationPreferencesQuery(undefined, {
        skip: !isOpen,
    });
    useEffect(() => {
        if (isError) {
            onSecondaryButtonClick();
        }
    }, [isError, onSecondaryButtonClick]);

    const preferences = useMemo(
        () =>
            !isFetching && data
                ? {
                      email: data.email,
                      inApp: data.in_app,
                      ...NewLiveReferralSettings.initialValues(data),
                      ...WorklistSummarySettings.initialValues(data),
                  }
                : undefined,
        [data, isFetching],
    );

    return (
        <NotificationSettingsDialog
            isOpen={isOpen}
            notificationTypes={notificationTypes}
            summaryEmailTypes={summaryEmailTypes}
            values={preferences}
            onSecondaryButtonClick={onSecondaryButtonClick}
            onSubmit={async (values) => {
                await postUpdateNotificationPreferences({
                    email: values.email,
                    in_app: values.inApp,
                    ...NewLiveReferralSettings.submitValues(values),
                    ...WorklistSummarySettings.submitValues(values),
                }).unwrap();
                SuccessToast({ message: 'Your notification settings were updated successfully.' });
                onSecondaryButtonClick();
            }}
            width={770}
        />
    );
};
